import api from './api';

// ─── Doctor Availability / Schedule Configuration ──────────────────────────

/**
 * GET /api/doctor/availability/working-days
 * Returns { monday, tuesday, wednesday, thursday, friday, saturday, sunday }
 */
export const getWorkingDays = async () => {
  const { data } = await api.get('/doctor/availability/working-days');
  return data;
};

/**
 * PUT /api/doctor/availability/working-days
 */
export const updateWorkingDays = async (workingDays) => {
  const { data } = await api.put('/doctor/availability/working-days', workingDays);
  return data;
};

/**
 * GET /api/doctor/availability/day?date=YYYY-MM-DD
 * Returns { date, workingDay, fullDayBlocked, blockReason, slots: [{time,label,period,status,patientName}] }
 */
export const getDaySchedule = async (date) => {
  const { data } = await api.get('/doctor/availability/day', { params: { date } });
  return data;
};

/**
 * POST /api/doctor/availability/slots/toggle  { date, time }
 * Flips a slot between AVAILABLE and BLOCKED. Throws if the slot is booked.
 */
export const toggleSlot = async (date, time) => {
  const { data } = await api.post('/doctor/availability/slots/toggle', { date, time });
  return data;
};

/**
 * POST /api/doctor/availability/blocks
 * body: { startDate, endDate, period: 'ALL_DAY'|'MORNING'|'AFTERNOON'|'CUSTOM', startTime, endTime, reason }
 */
export const blockPeriod = async (payload) => {
  const { data } = await api.post('/doctor/availability/blocks', payload);
  return data;
};

/**
 * GET /api/doctor/availability/blocks?start=&end=
 */
export const getBlocks = async (start, end) => {
  const { data } = await api.get('/doctor/availability/blocks', { params: { start, end } });
  return data || [];
};

export const deleteBlock = async (id) => {
  await api.delete(`/doctor/availability/blocks/${id}`);
};

/**
 * DELETE /api/doctor/availability/day?date=
 * Clears every block on that date (full-day + any individually blocked slots).
 */
export const unblockDay = async (date) => {
  const { data } = await api.delete('/doctor/availability/day', { params: { date } });
  return data;
};

/**
 * GET /api/doctor/availability/export?start=&end=
 * Returns raw appointment rows; caller turns this into a CSV download.
 */
export const getExportRows = async (start, end) => {
  const { data } = await api.get('/doctor/availability/export', { params: { start, end } });
  return data || [];
};

/**
 * Builds a CSV file from schedule rows and triggers a browser download.
 * Kept client-side so we don't need special blob-download handling through the auth client.
 */
export const downloadScheduleCsv = (rows, filename = 'schedule-export.csv') => {
  const headers = ['Date', 'Time', 'Patient', 'Phone', 'Type', 'Status', 'Fee'];
  const escape = (val) => {
    const s = val === null || val === undefined ? '' : String(val);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(',')];
  rows.forEach((r) => {
    lines.push([
      r.appointmentDate || '',
      r.slotTime || '',
      r.patientName || '',
      r.patientPhone || '',
      r.consultationType || '',
      r.status || '',
      r.fee ?? '',
    ].map(escape).join(','));
  });
  const csvContent = lines.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};